import cn from "classnames"
import React, { useEffect, useState } from "react"
import { getPriceWithCurrency, merchantSelectors, StandardButtonWithSpinner, useSelector } from "swiipe.portal.shared"
import IconRefresh from "../../images/icons/icon-refresh.svg"
import { orderSelectors } from "../../store/reducers/orderReducer"
import { partnerSelectors } from "../../store/reducers/partnerReducer"
import { StoreState } from "../../store/StoreState"
import {
    getTermsTechOrderDetailsThunk,
    getTermsTechOrderReferenceThunk,
    getTermsTechOrderStatusThunk,
    termsTechMockBuyerApproveInvoiceThunk,
    termsTechMockBuyerPayInThunk,
    termsTechMockCaptureTransaction,
    termsTechMockSellerPayOutThunk,
    termsTechMockTechOfficerApproveDocsThunk,
    termsTechMockUploadDocumentThunk,
} from "../../store/thunks/area42Thunks"
import { useReduxDispatch } from "../../store/useReduxDispatch"
import { Order } from "../../type/Order"
import { PaymentOrderDetails } from "../../type/paymentOrderDetails"
import { TransactionStatus, TransactionType } from "../../type/Transaction"
import "./TermsTechOrderHelper.scss"

interface ITermsTechOrderHelperProps {
    webshopId: string
    order: Order
}

interface ITermsTechOrderDetails {
    ignoreTestEnvWebhooks: boolean
}

export const TermsTechOrderHelper = ({ webshopId, order }: ITermsTechOrderHelperProps) => {
    const dispatch = useReduxDispatch()

    const hasMasterPartner = useSelector(partnerSelectors.partnersContainMasterPartner)
    const merchantDetails = useSelector(merchantSelectors.merchantDetails)

    const [orderRef, setOrderRef] = useState<string | undefined>(undefined)
    const [termsTechOrderStatus, setTermsTechOrderStatus] = useState<string | undefined>(undefined)
    const [isRefreshing, setIsRefreshing] = useState<boolean>(false)

    const [mockSuccessful, setMockSuccessful] = useState<boolean | undefined>(undefined)
    const [errorMessage, setErrorMessage] = useState<string>("")

    const [orderDetails, setOrderDetails] = useState<ITermsTechOrderDetails>()

    const paymentOrderDetails = useSelector<StoreState, PaymentOrderDetails | undefined>((state) =>
        orderSelectors.getPaymentOrderDetails(state, order.orderId)
    )
    const pendingCapture = paymentOrderDetails?.transactions?.find(
        (tx) => tx.transactionType == TransactionType.Capture && tx.transactionStatus == TransactionStatus.Pending
    )

    const fetchOrderStatusAtProvider = async (swMerchantId: string, orderRef: string) => {
        setIsRefreshing(true)
        try {
            const response = await dispatch(getTermsTechOrderStatusThunk(swMerchantId, orderRef, webshopId))
            setTermsTechOrderStatus(response.status ?? response.errorMessage ?? "error loading status")
        } catch {
            setTermsTechOrderStatus("error loading status")
        }
        setIsRefreshing(false)
    }

    useEffect(() => {
        setIsRefreshing(true)
        if (!hasMasterPartner || order.paymentType != "TermsTech" || !merchantDetails) {
            return
        }

        ;(async () => {
            const orderRefResponse = await dispatch(
                getTermsTechOrderReferenceThunk(merchantDetails.swMerchant.swMerchantId, webshopId, order.orderId)
            )
            setOrderRef(orderRefResponse.orderReference)
            fetchOrderStatusAtProvider(merchantDetails.swMerchant.swMerchantId, orderRefResponse.orderReference)

            const orderDetailsResponse = await dispatch(
                getTermsTechOrderDetailsThunk(merchantDetails.swMerchant.swMerchantId, orderRefResponse.orderReference)
            )
            setOrderDetails({ ignoreTestEnvWebhooks: orderDetailsResponse.ignoreTestEnvWebhooks })
        })()
    }, [order?.orderId])

    if (!hasMasterPartner || order.paymentType != "TermsTech" || !orderRef || !merchantDetails) {
        return null
    }

    return (
        <div className="terms-tech-order-helper">
            <p className="terms-tech-order-helper__heading">Terms.Tech functions</p>

            <div className="terms-tech-order-helper__utility-functions">
                <div className="terms-tech-order-helper__utility-functions-panel terms-tech-order-helper__utility-functions-panel--status">
                    <span
                        className={cn(
                            "terms-tech-order-helper__refresh-icon",
                            isRefreshing && "terms-tech-order-helper__refresh-icon--refresh-animating"
                        )}
                        onClick={async () => {
                            if (!termsTechOrderStatus || isRefreshing) {
                                return
                            }
                            await fetchOrderStatusAtProvider(merchantDetails.swMerchant.swMerchantId, orderRef)
                        }}
                    >
                        <img src={IconRefresh} alt={"Refresh"} />
                    </span>
                    <div className="terms-tech-order-helper__status-info">
                        <p className="terms-tech-order-helper__status-info-header">Status:</p>
                        <b className="terms-tech-order-helper__status-info-state">
                            {isRefreshing ? "loading..." : termsTechOrderStatus ?? "loading..."}
                        </b>
                    </div>
                    {orderDetails?.ignoreTestEnvWebhooks && (
                        <div className="terms-tech-order-helper__ignore-webhooks">
                            <p className="terms-tech-order-helper__ignore-webhooks-label">Ignores test environment webhooks</p>
                        </div>
                    )}
                </div>
                {!isRefreshing && (
                    <div className="terms-tech-order-helper__utility-functions-panel">
                        {(termsTechOrderStatus == "Ready For Shipping" ||
                            (termsTechOrderStatus == "Review Pending" && mockSuccessful == false)) && (
                            <StandardButtonWithSpinner
                                disabled={isRefreshing}
                                onClick={async () => {
                                    setIsRefreshing(true)
                                    try {
                                        const response = await dispatch(termsTechMockUploadDocumentThunk(orderRef, webshopId))
                                        setMockSuccessful(response.success)
                                        setErrorMessage(response.errorMessage)
                                    } catch {
                                        //Catch to stop showing spinner
                                    }
                                    fetchOrderStatusAtProvider(merchantDetails.swMerchant.swMerchantId, orderRef)
                                }}
                            >
                                Upload invoice and delivery slip
                            </StandardButtonWithSpinner>
                        )}
                        {termsTechOrderStatus == "Review Pending" && (
                            <StandardButtonWithSpinner
                                disabled={isRefreshing || mockSuccessful == false}
                                onClick={async () => {
                                    setIsRefreshing(true)
                                    try {
                                        const response = await dispatch(
                                            termsTechMockTechOfficerApproveDocsThunk(orderRef, webshopId, true)
                                        )
                                        setMockSuccessful(response.success)
                                        setErrorMessage(response.errorMessage)
                                    } catch {
                                        //Catch to stop showing spinner
                                    }
                                    fetchOrderStatusAtProvider(merchantDetails.swMerchant.swMerchantId, orderRef)
                                }}
                            >
                                Approve uploaded documents as Terms.Tech Officer
                            </StandardButtonWithSpinner>
                        )}
                        {termsTechOrderStatus == "Buyer Review Pending" && (
                            <StandardButtonWithSpinner
                                disabled={isRefreshing}
                                onClick={async () => {
                                    setIsRefreshing(true)
                                    try {
                                        const response = await dispatch(
                                            termsTechMockBuyerApproveInvoiceThunk(orderRef, webshopId, true)
                                        )
                                        setMockSuccessful(response.success)
                                        setErrorMessage(response.errorMessage)
                                    } catch {
                                        //Catch to stop showing spinner
                                    }
                                    fetchOrderStatusAtProvider(merchantDetails.swMerchant.swMerchantId, orderRef)
                                }}
                            >
                                Approve Invoice as Buyer
                            </StandardButtonWithSpinner>
                        )}
                        {termsTechOrderStatus == "Ready For Payout" && (
                            <StandardButtonWithSpinner
                                disabled={isRefreshing}
                                onClick={async () => {
                                    setIsRefreshing(true)
                                    try {
                                        const response = await dispatch(termsTechMockSellerPayOutThunk(orderRef, webshopId))
                                        setMockSuccessful(response.success)
                                        setErrorMessage(response.errorMessage)
                                    } catch {
                                        //Catch to stop showing spinner
                                    }
                                    fetchOrderStatusAtProvider(merchantDetails.swMerchant.swMerchantId, orderRef)
                                }}
                            >
                                Payout to the Seller
                            </StandardButtonWithSpinner>
                        )}
                        {termsTechOrderStatus == "Paid-out" && (
                            <StandardButtonWithSpinner
                                disabled={isRefreshing}
                                onClick={async () => {
                                    setIsRefreshing(true)
                                    try {
                                        const response = await dispatch(termsTechMockBuyerPayInThunk(orderRef, webshopId))
                                        setMockSuccessful(response.success)
                                        setErrorMessage(response.errorMessage)
                                    } catch {
                                        //Catch to stop showing spinner
                                    }
                                    fetchOrderStatusAtProvider(merchantDetails.swMerchant.swMerchantId, orderRef)
                                }}
                            >
                                Capture money from the buyer
                            </StandardButtonWithSpinner>
                        )}
                        {pendingCapture && (
                            <StandardButtonWithSpinner
                                disabled={isRefreshing}
                                onClick={async () => {
                                    setIsRefreshing(true)
                                    try {
                                        const response = await dispatch(
                                            termsTechMockCaptureTransaction(orderRef, pendingCapture.amount, order.currency)
                                        )
                                        setMockSuccessful(response.success)
                                        setErrorMessage(response.errorMessage)
                                    } catch {
                                        //Catch to stop showing spinner
                                    }
                                    fetchOrderStatusAtProvider(merchantDetails.swMerchant.swMerchantId, orderRef)
                                }}
                            >
                                Digiteal: transfer {getPriceWithCurrency(pendingCapture.amount, order.currency)} to merchant
                            </StandardButtonWithSpinner>
                        )}
                    </div>
                )}
                {mockSuccessful === false && (
                    <p className="terms-tech-order-helper__msg terms-tech-order-helper__msg--error">
                        {errorMessage != "" ? errorMessage : "An error has occurred..."}
                    </p>
                )}
            </div>
        </div>
    )
}
